import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

interface NewUserForm {
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}

interface StatusMessage {
  type: 'success' | 'error';
  text: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    _id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

export default function NewUser() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [formData, setFormData] = useState<NewUserForm>({
    name: '',
    email: '',
    role: 'user',
    isActive: true,
  });
  const [errors, setErrors] = useState<Partial<NewUserForm>>({});

  const createUserMutation = useMutation({
    mutationFn: async (userData: NewUserForm) => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: userData.name,
          email: userData.email,
          role: userData.role,
          isActive: userData.isActive ? 1 : 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear el usuario');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Error al crear el usuario');
      }

      return result;
    },
    onSuccess: (data) => {
      // Invalidate users list query to refresh data when returning to the list
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      // Reset form first
      setFormData({
        name: '',
        email: '',
        role: 'user',
        isActive: true,
      });

      // Then set success message
      setStatusMessage({
        type: 'success',
        text: 'Usuario creado exitosamente. Se han enviado las credenciales por correo electrónico.'
      });

      // Scroll to top to show the message
      window.scrollTo(0, 0);
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        text: error.message || 'Error al crear el usuario'
      });
      
      // Scroll to top to show the message
      window.scrollTo(0, 0);
    }
  });

  const validateForm = () => {
    const newErrors: Partial<NewUserForm> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.role) {
      newErrors.role = 'El rol es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await createUserMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/users')}
            className="flex items-center text-slate-600 hover:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver a Usuarios
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Nuevo Usuario</h1>
            <p className="text-sm text-slate-500 mb-6">
              Completa el formulario para crear un nuevo usuario
            </p>

            {statusMessage && (
              <div 
                className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                  statusMessage.text.includes('exitosamente') 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
                role="alert"
              >
                {statusMessage.text.includes('exitosamente') ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="flex-1">{statusMessage.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-slate-200'
                  }`}
                  placeholder="Ingresa el nombre completo"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-slate-200'
                  }`}
                  placeholder="ejemplo@correo.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rol
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.role ? 'border-red-500' : 'border-slate-200'
                  }`}
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                  <option value="viewer">Visualizador</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                )}
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-slate-700">Usuario activo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/users')}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                >
                  {statusMessage?.type === 'success' ? 'Volver a la Lista' : 'Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isPending || statusMessage?.type === 'success'}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createUserMutation.isPending 
                    ? 'Creando...' 
                    : statusMessage?.type === 'success'
                      ? '¡Usuario Creado!'
                      : 'Crear Usuario'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
} 